import React, { useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { useMarcarAlterado } from './AlteracaoDoWorkflow';

/**
 * Reordenar as linhas de uma tabela de etapa arrastando a alça (⋮⋮).
 *
 * POR PONTEIRO, E NÃO PELO DRAG-AND-DROP DO HTML. O arrastar nativo não existe
 * no toque, e é no celular, no curral, que a linha esquecida é percebida.
 * Eventos de ponteiro cobrem mouse, dedo e caneta com o mesmo código: a alça
 * captura o ponteiro, e a linha sob ele é achada por `elementFromPoint`.
 *
 * Só a ALÇA arrasta. A linha inteira está cheia de campos, e arrastar ao
 * tentar selecionar um texto seria pior que não ter o recurso.
 *
 * Teclado: com a alça em foco, ↑ e ↓ movem a linha uma casa.
 *
 * `onMover(de, para)` recebe os índices; a aba aplica `moverLinha` no array
 * dela. O resultado só vai para o banco no "Salvar Workflow", e por isso o
 * hook marca o workflow como alterado — fechar sem salvar avisa.
 */
export const useArrastarLinhas = (onMover) => {
  const marcarAlterado = useMarcarAlterado();
  const [origem, setOrigem] = useState(null);
  const [alvo, setAlvo] = useState(null);
  // O laço do ponteiro lê daqui: entre dois renders, o estado pode estar velho.
  const atual = useRef({ origem: null, alvo: null, grupo: null });

  const limpar = () => {
    atual.current = { origem: null, alvo: null, grupo: null };
    setOrigem(null);
    setAlvo(null);
  };

  const mover = (de, para) => {
    if (de === null || para === null || de === para) return;
    onMover(de, para);
    marcarAlterado();
  };

  const alca = (indice, total) => ({
    onPointerDown: (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      atual.current = {
        origem: indice, alvo: indice,
        grupo: e.currentTarget.closest('[data-arrastar-grupo]') || e.currentTarget.closest('tbody'),
      };
      setOrigem(indice);
      setAlvo(indice);
    },
    onPointerMove: (e) => {
      if (atual.current.origem === null) return;
      const sob = document.elementFromPoint?.(e.clientX, e.clientY);
      const linha = sob?.closest?.('[data-linha-arrastavel]');
      if (!linha || !atual.current.grupo?.contains(linha)) return;
      const i = Number(linha.getAttribute('data-linha-arrastavel'));
      if (Number.isInteger(i) && i !== atual.current.alvo) {
        atual.current.alvo = i;
        setAlvo(i);
      }
    },
    onPointerUp: () => {
      const { origem: de, alvo: para } = atual.current;
      limpar();
      mover(de, para);
    },
    onPointerCancel: limpar,
    onKeyDown: (e) => {
      if (e.key === 'ArrowUp' && indice > 0) {
        e.preventDefault();
        mover(indice, indice - 1);
      } else if (e.key === 'ArrowDown' && indice < total - 1) {
        e.preventDefault();
        mover(indice, indice + 1);
      }
    },
  });

  // A linha arrastada fica apagada; a de destino ganha um traço azul do lado
  // para onde a arrastada vai entrar.
  const linha = (indice) => {
    let realce = '';
    if (origem === indice) realce = 'opacity-40';
    else if (origem !== null && alvo === indice) {
      realce = alvo < origem
        ? 'shadow-[inset_0_2px_0_0_#2563eb]'
        : 'shadow-[inset_0_-2px_0_0_#2563eb]';
    }
    return { 'data-linha-arrastavel': indice, realce };
  };

  return { alca, linha, arrastando: origem !== null };
};

/** A alça ⋮⋮ da linha. `props` vem de `alca(indice, total)`. */
export const AlcaDeArrastar = ({ props: alcaProps, rotulo = 'linha' }) => (
  <button
    type="button"
    {...alcaProps}
    aria-label={`Arrastar ${rotulo} para reordenar (setas ↑ ↓ também movem)`}
    title="Arraste para mudar a ordem"
    className="p-0.5 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
    style={{ touchAction: 'none' }}
  >
    <GripVertical className="w-3.5 h-3.5" />
  </button>
);
